import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
  Tooltip
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import type { ControllerRenderProps } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_THREE_GTMS_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import { useMutationWithToast } from '../../../hooks/use-mutation-with-toast'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type TThreeGTMSAppActionEnum = 'GenerateApiKey' | 'RemoveSftp' | 'UpsertSftp'

type TEnableThreeGTMSAppInput = {
  action: TThreeGTMSAppActionEnum
  appType: AppTypeEnum
  host?: string
  password?: string
  port?: number
  username?: string
}

const formSchema = z.object({
  host: z.string().min(1, 'SFTP host is required'),
  password: z.string().min(1, 'Password is required'),
  port: z.coerce.number().int().min(1, 'Port must be a positive number').max(65535, 'Port must be at most 65535'),
  username: z.string().min(1, 'Username is required')
})

type TFormValues = z.infer<typeof formSchema>

/**
 * Modal for enabling the 3GTMS app.
 * Provides two independent actions:
 * 1. Upsert SFTP credentials - verifies SFTP credentials and saves them
 * 2. Generate API key - creates or returns an existing Chain API key
 * 3. Remove SFTP credentials in update mode while keeping API key
 */
export function ThreeGTMSModal({ isOpen, onClose, appType, app, isModalEditable, setModalEditable }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [generatedApiKey, setGeneratedApiKey] = useState('')
  const [isSFTPConfiguredLocally, setIsSFTPConfiguredLocally] = useState(false)
  const [isSFTPRemovedLocally, setIsSFTPRemovedLocally] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      host: '',
      password: '',
      port: 22,
      username: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [enableThreeGTMSApp, { loading: sftpLoading }] = useMutation<
    { enableThreeGTMSApp: { appEnabled: boolean; chaineAPIToken?: string } },
    TEnableThreeGTMSAppInput
  >(ENABLE_THREE_GTMS_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    refetchQueries: [GET_APPS]
  })

  const executeWithToast = useMutationWithToast<
    { enableThreeGTMSApp: { appEnabled: boolean; chaineAPIToken?: string } },
    TEnableThreeGTMSAppInput
  >({
    mutationFn: enableThreeGTMSApp,
    onError: (err) => setFormError(err.message)
  })

  const handleClose = () => {
    setFormError('')
    setGeneratedApiKey('')
    setIsSFTPConfiguredLocally(false)
    setIsSFTPRemovedLocally(false)
    setModalEditable?.(false)
    form.reset()
    onClose()
  }

  const runThreeGTMSAction = async (params: {
    action: TThreeGTMSAppActionEnum
    host?: string
    loadingMessage?: string
    password?: string
    port?: number
    successMessage: string
    username?: string
  }): Promise<boolean> => {
    const { action, host, loadingMessage, password, port, successMessage, username } = params
    setFormError('')

    try {
      const result = await executeWithToast({
        loadingMessage,
        successMessage,
        variables: {
          action,
          appType: appType as AppTypeEnum,
          host,
          password,
          port,
          username
        }
      })

      const apiKey = result?.data?.enableThreeGTMSApp?.chaineAPIToken
      if (apiKey) {
        setGeneratedApiKey(apiKey)
      }

      return true
    } catch {
      return false
    }
  }

  const onSubmitSFTP = async (values: z.infer<typeof formSchema>) => {
    const isSuccess = await runThreeGTMSAction({
      action: 'UpsertSftp',
      host: values.host,
      loadingMessage: 'Saving...',
      password: values.password,
      port: values.port,
      successMessage: 'SFTP credentials verified and saved.',
      username: values.username
    })
    if (isSuccess) {
      setIsSFTPConfiguredLocally(true)
      setIsSFTPRemovedLocally(false)
    }
  }

  const handleCreateAPIKey = async () => {
    await runThreeGTMSAction({
      action: 'GenerateApiKey',
      loadingMessage: 'Generating...',
      successMessage: 'API key is ready.'
    })
  }

  const handleRemoveSFTP = async () => {
    const isSuccess = await runThreeGTMSAction({
      action: 'RemoveSftp',
      loadingMessage: 'Removing...',
      successMessage: 'SFTP credentials removed. API key is still available.'
    })
    if (isSuccess) {
      setIsSFTPConfiguredLocally(false)
      setIsSFTPRemovedLocally(true)
    }
  }

  const hasExistingSFTP = !!app?.workspaceApp?.appCredentials?.password && !!app?.workspaceApp?.appCredentials?.username
  const hasSFTPConfigured = isSFTPConfiguredLocally || (hasExistingSFTP && !isSFTPRemovedLocally)
  const displayedApiKey = generatedApiKey || app?.workspaceApp?.appCredentials?.chaineAPIToken
  const isConfigured = !!displayedApiKey || hasSFTPConfigured
  const isUpdating = !!isModalEditable
  const submitButtonLabel = hasSFTPConfigured ? 'Update SFTP' : 'Connect SFTP'
  const isLoading = sftpLoading

  const getDialogTitle = (): string => {
    if (isUpdating) return 'Update 3GTMS integration'
    if (isConfigured) return '3GTMS is configured for your workspace'
    return 'Enable 3GTMS for Chain'
  }

  const getDialogDescription = (): string => {
    if (isUpdating)
      return 'Update SFTP settings, remove SFTP, or generate an API key without losing existing configuration.'
    if (isConfigured) return 'You can update SFTP and API key settings at any time.'
    return 'Set up 3GTMS using SFTP, API key, or both. You can complete setup in any order.'
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        {!!formError && <Alert variant="error" description={formError} />}

        <Alert
          variant="warning"
          description={
            <span>
              Does your SFTP server restrict access by IP address? Contact{' '}
              <a href="mailto:support@trychain.com" className="font-medium underline">
                support@trychain.com
              </a>{' '}
              to get the Chain IP address you need to allowlist before connecting.
            </span>
          }
        />

        <Form {...form}>
          <div className="space-y-4">
            <p className="text-sm font-medium">SFTP Connection Details</p>

            <FormField
              control={form.control}
              name="host"
              render={({ field }: { field: ControllerRenderProps<TFormValues, 'host'> }) => (
                <FormItem>
                  <FormLabel>Host</FormLabel>
                  <FormControl>
                    <Input placeholder="sftp.example.com" {...field} disabled={isLoading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="port"
              render={({ field }: { field: ControllerRenderProps<TFormValues, 'port'> }) => (
                <FormItem>
                  <FormLabel>Port</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="22" {...field} disabled={isLoading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="username"
              render={({ field }: { field: ControllerRenderProps<TFormValues, 'username'> }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your SFTP username" {...field} disabled={isLoading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }: { field: ControllerRenderProps<TFormValues, 'password'> }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Enter your SFTP password" {...field} disabled={isLoading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-2">
              <Button type="button" disabled={isLoading} onClick={form.handleSubmit(onSubmitSFTP)} className="w-full">
                {isLoading ? 'Saving...' : submitButtonLabel}
              </Button>

              {!!isUpdating && !!hasSFTPConfigured && (
                <Button
                  type="button"
                  variant="destructive"
                  className="w-full"
                  disabled={isLoading}
                  onClick={handleRemoveSFTP}
                >
                  {isLoading ? 'Removing...' : 'Remove SFTP'}
                </Button>
              )}
            </div>

            <p className="-mt-1 text-center text-xs leading-none text-muted-foreground">
              Scroll down to generate or view your API key.
            </p>
          </div>
        </Form>

        <Separator className="my-2" />

        <div className="space-y-3">
          <p className="text-sm font-medium">Chain API Key</p>

          {displayedApiKey ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Provide this API key to 3GTMS to authenticate requests to Chain.
              </p>
              <div className="rounded-md border bg-muted px-3 py-2 font-mono text-sm break-all">{displayedApiKey}</div>
              <div className="flex justify-end">
                <Tooltip content="Copy to clipboard">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(displayedApiKey)
                      toast.success('API key copied to clipboard!')
                    }}
                  >
                    Copy
                  </Button>
                </Tooltip>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Generate an API key for 3GTMS to use when sending data to Chain.
              </p>
              <Button type="button" variant="outline" onClick={handleCreateAPIKey} disabled={isLoading}>
                {isLoading ? 'Generating...' : 'Generate API Key'}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
          {!!app?.docs && (
            <a
              href={app.docs}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              Read the integration guide
              <ExternalLink className="h-4 w-4" />
            </a>
          )}

          <Button variant="outline" onClick={handleClose} type="button">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
